/**
 * Dashboard skeletons.
 *
 * Two levels, so the page can hold its real header (greeting + input) while
 * only the sections below it are still loading:
 *
 *   - `DashboardHomeSkeleton` — the whole page (header + sections). Rendered
 *     only while the org itself is unresolved, i.e. before the real header can
 *     exist.
 *   - `DashboardSectionsSkeleton` — the three sections only. Rendered by the
 *     real dashboard under its real header while the onboarding/import gate is
 *     open, so the ConversationInput never remounts across loading → content.
 *
 * The row pieces (`MeetingRowSkeleton`, `ActionRowSkeleton`,
 * `PipelineGlanceSkeleton`) are the SAME components the live sections render
 * while their own data is in flight — one implementation, so the loading and
 * loaded layouts cannot drift apart.
 *
 * CRITICAL: Container dimensions (max-w, padding, gaps, row heights) must
 * EXACTLY match the real components to prevent layout shift when content loads.
 *
 * Dimension sources:
 * - Outer container: components/DashboardHome.tsx
 * - Header: DashboardHeader.tsx
 * - Meetings: MeetingsSection.tsx (header + MeetingRow)
 * - Actions: ActionsSection.tsx (header + ActionRow)
 * - Pipeline: PipelineGlance.tsx
 */

import '../dashboard.css';

import { useCallback, useEffect, useMemo, useState } from 'react';

// No-op handler so ConversationInput's enableFileUpload condition (!!orgId && !!onFilesChange)
// is satisfied and the paperclip button renders in the skeleton.
// Trade-off: if the user uploads a file during the skeleton phase, the upload
// succeeds server-side but the returned file ID is discarded here — state lives
// in the loaded DashboardHeader. The file will appear to vanish on the
// skeleton→content transition. This is an acceptable visual trade-off; the
// acceptance criteria mandates showing the paperclip for layout parity.
const NOOP_FILES_CHANGE = () => {};

import { useUser } from '@/allauth_auth/hooks.js';
import { markOnce } from '@/lib/boot/bootMetrics';
import { PROMPT_BAR_GLOW_CLASS } from '@/lib/components/promptBarGlow';
import { getPersistedDashboardFoldCounts } from '@/lib/localStorage/persistedDashboardFold';
import { getPersistedUserProfile } from '@/lib/localStorage/persistedUserProfile';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import {
  getGreeting,
  getTimePeriod,
} from '@/pages/dashboard/hooks/useDashboardData';

import { MAX_VISIBLE_ACTIONS } from './constants';

/** Rows shown when nothing is known about the org's data yet. */
const DEFAULT_MEETING_ROWS = 3;
const DEFAULT_ACTION_ROWS = MAX_VISIBLE_ACTIONS;

const PIPELINE_BUCKET_TITLES = ['Early stage', 'Moving', 'About to close'];

/** Skeleton row for meetings — matches MeetingRow (bg-primary, border, h-14 rounded-[4px]) */
export function MeetingRowSkeleton() {
  return (
    <div className='h-14 bg-primary border border-secondary rounded-[4px] animate-pulse' />
  );
}

/** Skeleton row for actions — matches ActionRow (bg-primary, border, h-14 rounded-[4px]) */
export function ActionRowSkeleton() {
  return (
    <div className='h-14 bg-primary border border-secondary rounded-[4px] animate-pulse' />
  );
}

/**
 * Skeleton for the pipeline section — matches PipelineGlance's bucketed
 * layout (mobile single card, desktop three columns). Rendered by
 * PipelineGlance itself while its metrics load, and by the page skeletons.
 */
export function PipelineGlanceSkeleton() {
  return (
    <div className='space-y-3'>
      {/* Title — matches: text-[14px] font-semibold */}
      <div className='h-[14px] w-36 bg-tertiary rounded animate-pulse' />
      {/* Mobile: compact single-card — matches PipelineSectionMobile */}
      <div className='sm:hidden'>
        <div className='bg-primary border border-secondary rounded-[4px] px-4 py-3'>
          <div className='grid grid-cols-3 gap-2'>
            {PIPELINE_BUCKET_TITLES.map(title => (
              <div key={title}>
                <div className='text-[12px] font-semibold text-tertiary'>
                  {title}
                </div>
                <div className='h-5 w-14 bg-tertiary rounded animate-pulse mt-0.5' />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Desktop: three-column cards — matches PipelineColumn */}
      <div className='hidden sm:grid sm:grid-cols-3 sm:gap-6'>
        {PIPELINE_BUCKET_TITLES.map(title => (
          <div
            key={title}
            className='bg-primary border border-secondary rounded-[4px] p-4'
          >
            <div className='text-[13px] font-semibold text-tertiary'>
              {title}
            </div>
            <div className='h-5 w-20 bg-tertiary rounded animate-pulse mt-1' />
            <div className='divide-y divide-border-secondary mt-3'>
              {[1, 2].map(i => (
                <div key={i} className='flex items-center justify-between py-2'>
                  <div className='h-[14px] w-20 bg-tertiary rounded animate-pulse' />
                  <div className='h-[14px] w-14 bg-tertiary rounded animate-pulse' />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Which loading stage is rendering a skeleton. Recorded once as a
 * `stage:skeleton:<stage>` boot mark so the perf trace shows how long each
 * stage was on screen (lib/perf/perfTrace.ts).
 */
export type DashboardSkeletonStage = 'org-loading' | 'sections-loading';

interface DashboardSectionsSkeletonProps {
  /** Org whose persisted row counts shape the skeleton. */
  orgId?: number;
  stage?: DashboardSkeletonStage;
}

function rowKeys(count: number, prefix: string): string[] {
  return Array.from({ length: count }, (_, i) => `${prefix}-${i}`);
}

/**
 * The three content sections as skeletons — matches the `space-y-6` column in
 * components/DashboardHome.tsx. Row counts come from the last visit's persisted
 * fold snapshot when there is one, so the skeleton is the right height and the
 * transition to content is a fade rather than a reflow.
 */
export function DashboardSectionsSkeleton({
  orgId,
  stage,
}: DashboardSectionsSkeletonProps) {
  useEffect(() => {
    if (stage) {
      markOnce(`stage:skeleton:${stage}`);
    }
  }, [stage]);

  const { meetingRows, actionRows } = useMemo(() => {
    const counts = getPersistedDashboardFoldCounts(orgId);
    const meetingsKnown =
      counts === undefined
        ? DEFAULT_MEETING_ROWS
        : counts.meetings + counts.reminders + counts.tasks;
    const actionsKnown =
      counts === undefined ? DEFAULT_ACTION_ROWS : counts.proposals;
    return {
      // Always at least one row so an empty section still reserves its header.
      meetingRows: Math.max(1, Math.min(meetingsKnown, DEFAULT_MEETING_ROWS)),
      actionRows: Math.max(1, Math.min(actionsKnown, MAX_VISIBLE_ACTIONS)),
    };
  }, [orgId]);

  return (
    <div className='space-y-6'>
      {/* Meetings section — matches MeetingsSection layout */}
      <div className='space-y-3'>
        {/* Header row — matches: flex items-center justify-between mt-0.5 mb-3 */}
        <div className='flex items-center justify-between mt-0.5'>
          <div className='h-[15px] w-28 bg-tertiary rounded animate-pulse' />
          <div className='h-3 w-20 bg-tertiary rounded animate-pulse' />
        </div>
        <div className='space-y-1.5'>
          {rowKeys(meetingRows, 'meeting').map(key => (
            <MeetingRowSkeleton key={key} />
          ))}
        </div>
      </div>

      {/* Actions section — matches ActionsSection layout */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between mt-0.5'>
          <div className='h-[15px] w-20 bg-tertiary rounded animate-pulse' />
          <div className='h-3 w-24 bg-tertiary rounded animate-pulse' />
        </div>
        <div className='space-y-1.5'>
          {rowKeys(actionRows, 'action').map(key => (
            <ActionRowSkeleton key={key} />
          ))}
        </div>
      </div>

      <PipelineGlanceSkeleton />
    </div>
  );
}

interface DashboardHomeSkeletonProps {
  inputValue?: string;
  onInputChange?: (value: string) => void;
  orgId?: number;
  stage?: DashboardSkeletonStage;
}

/**
 * Whole-page skeleton — header + sections — matching DashboardHome exactly.
 * Only for the window before the org resolves; once it has, the real
 * DashboardHeader renders and only DashboardSectionsSkeleton is used.
 *
 * When orgId is provided, file upload is enabled (paperclip shows). Uploads
 * go through NOOP_FILES_CHANGE so the button renders for visual parity with
 * the loaded state. See NOOP_FILES_CHANGE above for the trade-off details.
 */
export function DashboardHomeSkeleton({
  inputValue,
  onInputChange,
  orgId,
  stage,
}: DashboardHomeSkeletonProps) {
  const [localValue, setLocalValue] = useState('');
  const isControlled = inputValue !== undefined;
  const greeting = useMemo(() => getGreeting(getTimePeriod()), []);

  // Best-effort first name from synchronous sources only — the persisted
  // user-profile cache (populated on the previous successful load) and the
  // auth user's `display` (available as soon as the session restores). When
  // both are unavailable (first-ever visit, signed-out tests), we fall back
  // to the existing shimmer. Reading the persisted cache requires the auth
  // user id so a shared device cannot leak the previous user's name.
  const authUser = useUser() as
    | { id?: number; display?: string }
    | null
    | undefined;
  const authUserId = authUser?.id;
  const authUserDisplay = authUser?.display;
  const firstName = useMemo<string | null>(() => {
    if (typeof authUserId === 'number') {
      const persisted = getPersistedUserProfile(authUserId);
      const persistedFirst = persisted?.user?.first_name;
      if (persistedFirst) {
        return persistedFirst;
      }
    }
    if (authUserDisplay) {
      const first = authUserDisplay.split(' ')[0];
      if (first) {
        return first;
      }
    }
    return null;
  }, [authUserId, authUserDisplay]);
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
  }, []);
  const handleChange = useCallback(
    (value: string) => {
      if (isControlled) {
        onInputChange?.(value);
      } else {
        setLocalValue(value);
      }
    },
    [isControlled, onInputChange]
  );

  return (
    <>
      {/* Outer container — matches DashboardHome: w-full px-4 sm:px-6 pt-4 sm:pt-12 pb-4 sm:pb-8 mx-auto max-w-[750px] */}
      <div
        data-testid='v2-skeleton'
        data-component='dashboard-skeleton'
        data-state='loading'
        className='relative w-full px-4 sm:px-6 pt-4 sm:pt-12 pb-4 sm:pb-8 mx-auto max-w-[750px] z-10'
      >
        {/* Header — matches DashboardHeader: flex-col items-start + className='mb-8' */}
        <div className='flex flex-col items-start mb-8'>
          {/* Greeting — matches: text-[22px] sm:text-[24px] font-medium mb-3.
              When persisted user-profile cache or auth display name is
              available we render the real first name in place of the
              shimmer to avoid the "Good morning, ___ → Good morning, Kevin"
              flash on cold load.

              IMPORTANT: when we render the name, format the string exactly
              like DashboardHeader's loaded path (`${greeting}, ${firstName}`
              with a regular ASCII space). The shimmer fallback uses `&nbsp;`
              so the text doesn't wrap before the placeholder block, but a
              non-breaking space renders 1–2px wider than a regular space in
              Roobert — using `&nbsp;` on the loaded path causes the name to
              visibly shift when the real header takes over. */}
          <h1 className='text-[22px] sm:text-[24px] font-medium text-primary mb-3'>
            {firstName ? (
              `${greeting}, ${firstName}`
            ) : (
              <>
                {greeting},&nbsp;
                <span className='inline-block w-20 h-5 bg-tertiary rounded animate-pulse align-middle' />
              </>
            )}
          </h1>

          {/* Subtitle — matches DashboardHeader's `text-[14px] mb-6` paragraph.
              ONE line, not two. The real subtitle ("It's Wednesday, August 5.
              You have 3 meetings today and 5 recommended actions to review.")
              fits a single line at this container's 750px cap, so the previous
              two-line shimmer measured ~38px and collapsed to ~21px the moment
              the header loaded, jumping everything below it up. It only wraps
              on narrow viewports, where under-claiming height is the cheaper
              error — content settling downward reads as loading, content
              snapping upward reads as a glitch.
              The 21px wrapper is the real line box (14px at normal leading);
              the 16px bar sits centred inside it so the height matches exactly
              while the shimmer stays visually proportioned to the text. */}
          <div className='mb-6 flex h-[21px] items-center'>
            <div className='h-4 w-4/5 bg-tertiary rounded animate-pulse' />
          </div>

          {/* Input — real ConversationInput so users can type during loading.
              autoFocus matches the real DashboardHeader's behavior so focus
              transfers cleanly across the skeleton→content transition and the
              user can start typing immediately on page load. */}
          <div className='w-full'>
            <ConversationInput
              value={isControlled ? inputValue : localValue}
              onChange={handleChange}
              onSubmit={handleSubmit}
              placeholder='Draft an email, research a prospect, update a deal'
              variant='simple'
              sticky={false}
              compactUntilWrap
              autoFocus
              orgId={orgId}
              onFilesChange={NOOP_FILES_CHANGE}
              enableVoice
              className={PROMPT_BAR_GLOW_CLASS}
            />
          </div>
        </div>

        <DashboardSectionsSkeleton orgId={orgId} stage={stage} />
      </div>
    </>
  );
}
